const DECIMALS = 2

export const formatToCurrency = (amount) => `£${amount.toFixed(DECIMALS)}`

const getMonthlyTotal = (prices) => prices.reduce((acc, { price }) => acc + price, 0)

const getUpfrontCost = (upfrontComponents) =>
  upfrontComponents.reduce((acc, { amount, conditional }) => (conditional ? acc : acc + amount), 0)

const getTotalMonthlyCost = (priceBreakdown) =>
  priceBreakdown.reduce((acc, { start, end, prices }) => acc + getMonthlyTotal(prices) * (end - start), 0)

const getContractLength = (priceBreakdown) =>
  priceBreakdown.reduce((end, { end: periodEnd }) => Math.max(end, periodEnd), 0)

export const generateUpfrontCosts = (upfrontComponents) => ({
  heading: 'Setup costs',
  data: upfrontComponents.map(({ title, amount, conditional }) => [
    `${title}${conditional ? ' (if required)' : ''}`,
    formatToCurrency(amount)
  ])
})

export const generateSetupCosts = (upfrontComponents) => {
  const total = upfrontComponents.reduce((total, { conditional, amount } = upfrontComponents) => {
    if (conditional === false) total += amount
    return total
  }, 0)

  const titles = {
    hardware: 'Equipment',
    postage: 'Delivery',
    activation: 'Activation',
    installation: 'Installation'
  }

  const getDetails = (type) => {
    const setupCost = upfrontComponents.find((setupCost) => setupCost.type === type)
    return {
      title: titles[type] + (setupCost?.conditional === true ? ' (if required)' : ''),
      amount: setupCost ? formatToCurrency(setupCost.amount) : '£0.00'
    }
  }

  const setupCosts = Object.keys(titles).reduce((acc, type) => {
    acc.push(getDetails(type))
    return acc
  }, [])

  return { setupCostTotal: formatToCurrency(total), setupCosts }
}

export const generatePriceBreakdown = (start, end, prices) => {
  const priceBreakdown = {
    heading: `Monthly payments (Months ${start + 1} - ${end})`,
    data: prices.map(({ title, price }) => [title, formatToCurrency(price)])
  }

  const totalMonthlyCost = getMonthlyTotal(prices)

  priceBreakdown.data.push(['Total', formatToCurrency(totalMonthlyCost)])

  return priceBreakdown
}

export const generateMonthlyCosts = (priceBreakdown) =>
  priceBreakdown.map(({ start, end, prices }) => generatePriceBreakdown(start, end, prices))

export const generateSimplifiedMonthlyCosts = (priceBreakdown) =>
  priceBreakdown.map(({ start, end, prices }) => [
    `Months ${start + 1} - ${end}`,
    formatToCurrency(getMonthlyTotal(prices))
  ])

export const generateAverageMonthlyCost = (upfrontComponents, priceBreakdown) => {
  const totalUpfrontCost = getUpfrontCost(upfrontComponents)
  const totalMonthlyCost = getTotalMonthlyCost(priceBreakdown)
  const contractLengthInMonths = getContractLength(priceBreakdown)
  const averageMonthlyCost = (totalUpfrontCost + totalMonthlyCost) / contractLengthInMonths
  return {
    heading: 'Average monthly cost',
    data: [
      [
        `For the ${contractLengthInMonths} month contract period, including any line rental and setup costs.`,
        formatToCurrency(averageMonthlyCost)
      ]
    ]
  }
}

export const generateFullContractCost = (upfrontComponents, priceBreakdown) => {
  const totalUpfrontCost = getUpfrontCost(upfrontComponents)
  const totalMonthlyCost = getTotalMonthlyCost(priceBreakdown)
  const contractLengthInMonths = getContractLength(priceBreakdown)
  const fullContractCost = totalUpfrontCost + totalMonthlyCost

  return {
    heading: 'Full contract cost',
    data: [
      [
        `Total cost for the ${contractLengthInMonths} month contract period, including any line rental and setup costs. Does not include any annual price rises.`,
        formatToCurrency(fullContractCost)
      ]
    ]
  }
}

export const generateTerms = (terms) => {
  const generatedTerms = {}

  if (terms?.length) {
    generatedTerms.infoList = {
      heading: 'Terms',
      data: terms.map(({ detail }) => detail)
    }
  }

  return generatedTerms
}

export const generateLink = (clickoutLinks) => {
  const generatedLink = {}

  if (clickoutLinks?.length) {
    generatedLink.link = {
      text: 'Get this deal',
      url: clickoutLinks[0].url
    }
  }

  return generatedLink
}

export const generatePriceDetails = (
  { title, price: { upfrontComponents }, priceBreakdown, terms, clickoutLinks },
  src,
  providerName
) => ({
  header: {
    heading: 'What will I pay?',
    image: {
      src,
      alt: providerName,
      height: 40
    },
    title
  },
  infoTables: [
    generateUpfrontCosts(upfrontComponents),
    ...generateMonthlyCosts(priceBreakdown),
    generateAverageMonthlyCost(upfrontComponents, priceBreakdown),
    generateFullContractCost(upfrontComponents, priceBreakdown)
  ],
  ...generateLink(clickoutLinks),
  ...generateTerms(terms)
})

export const generateCostsBreakdown = ({ price: { upfrontComponents }, priceBreakdown }) => ({
  setupCosts: generateSetupCosts(upfrontComponents),
  monthlyCosts: generateSimplifiedMonthlyCosts(priceBreakdown),
  averageMonthlyCost: generateAverageMonthlyCost(upfrontComponents, priceBreakdown).data[0][1],
  totalContractCost: generateFullContractCost(upfrontComponents, priceBreakdown).data[0][1]
})

export default { generatePriceDetails, generateCostsBreakdown }
